import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckSquare, Clock, AlertTriangle, Users, Info, ChevronDown } from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';

export const QueueExplanation: React.FC = () => {
  const { permissions } = useUserRole();

  // Only show coach-specific queue explanation for coaches and team owners
  if (!permissions.isCoach && !permissions.isTeamOwner && !permissions.canCreateGames) {
    return null;
  }

  return (
    <Card className="mb-6 bg-card border-border shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-3 text-foreground text-lg sm:text-xl">
          <div className="p-2 bg-primary/10 rounded-lg">
            <CheckSquare className="h-5 w-5 text-primary" />
          </div>
          <div>
            <span className="block">My Queue - Coach Task Management</span>
            <span className="text-sm font-normal text-muted-foreground block sm:hidden">
              Your action items organized by priority
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main explanation */}
        <div className="bg-muted/30 p-4 rounded-lg border-l-4 border-l-primary">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm text-foreground">
              <p className="font-medium mb-2">
                Your queue shows all tasks requiring your attention as a coach.
              </p>
              <p className="text-muted-foreground">
                Items are organized by priority to help you focus on urgent matters first. 
                Complete tasks to keep your team running smoothly.
              </p>
            </div>
          </div>
        </div>
        
        {/* Expandable Priority Levels & Examples */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="priority-guide" className="border border-border rounded-lg">
            <AccordionTrigger className="px-4 py-3 hover:no-underline bg-slate-800 hover:bg-slate-700 text-white rounded-t-lg data-[state=open]:rounded-b-none border-b border-slate-600 data-[state=open]:border-b-slate-600">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-green-500/20 rounded-md">
                  <ChevronDown className="h-4 w-4 text-green-400" />
                </div>
                <span className="font-semibold text-white text-sm">
                  Priority Levels & Examples
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                {/* Urgent Priority */}
                <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="destructive" className="text-xs font-medium px-2 py-1">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Urgent
                    </Badge>
                    <span className="text-sm font-medium text-foreground">Immediate Action Required</span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1 ml-1">
                    <p>• <strong>Game Starting Soon:</strong> Final roster check within 2 hours</p>
                    <p>• <strong>Roster Deadline:</strong> Less than 24 hours to submit lineup</p>
                    <p>• <strong>Confirmed Games:</strong> Games requiring roster management</p>
                    <p>• <strong>Ready to Play:</strong> Games with both teams confirmed</p>
                  </div>
                </div>

                {/* High Priority */}
                <div className="p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="secondary" className="text-xs font-medium px-2 py-1 bg-orange-500/10 text-orange-700 dark:text-orange-300">
                      <Clock className="h-3 w-3 mr-1" />
                      High
                    </Badge>
                    <span className="text-sm font-medium text-foreground">Time-Sensitive Tasks</span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1 ml-1">
                    <p>• <strong>Team Confirmation:</strong> Pending venue games needing confirmation</p>
                    <p>• <strong>Scorekeeper Apps:</strong> Review applications for upcoming games</p>
                    <p>• <strong>Upcoming Games:</strong> Games within 48 hours needing prep</p>
                    <p>• <strong>Player Eligibility:</strong> Review participation requests</p>
                  </div>
                </div>

                {/* Normal Priority */}
                <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="outline" className="text-xs font-medium px-2 py-1 border-blue-500/30 text-blue-700 dark:text-blue-300">
                      <Users className="h-3 w-3 mr-1" />
                      Normal
                    </Badge>
                    <span className="text-sm font-medium text-foreground">Regular Management</span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1 ml-1">
                    <p>• <strong>Game Submissions:</strong> Review and submit for approval</p>
                    <p>• <strong>Open Applications:</strong> Games available for scoring</p>
                    <p>• <strong>Team Organization:</strong> General roster management</p>
                    <p>• <strong>Future Planning:</strong> Games more than 48 hours away</p>
                  </div>
                </div>
                
                {/* All Priority Info */}
                <div className="flex items-center gap-3 p-3 bg-muted/30 border border-border rounded-lg">
                  <Badge variant="secondary" className="text-xs font-medium px-2 py-1">
                    All
                  </Badge>
                  <span className="text-sm text-foreground">Complete overview of all tasks across priority levels</span>
                </div>

                {/* Mobile tip */}
                <div className="sm:hidden bg-primary/5 p-3 rounded-lg border border-primary/20">
                  <p className="text-xs text-primary font-medium">
                    💡 Tip: Tap any priority tab above to filter your tasks
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};